import { ReactNode, useId } from "react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Button } from "../Button";
import { MenuSurface, MenuSurfaceItem, MenuSurfaceLink } from "../MenuSurface";
import { usePhone } from "../usePhone";
import s from "./ActionMenu.module.scss";

export type ActionItem =
  | { id: string; label: string; hint?: ReactNode; /** The hint as a small second line under the label (not at the end of the row). */ hintBelow?: boolean; icon?: ReactNode; danger?: boolean; href?: string; onSelect?: () => void }
  | { divider: true }
  | { radioGroup: { label: string; value: string; options: { value: string; label: string }[]; onChange: (value: string) => void } };

type Props = {
  label: string; anchorEl: HTMLElement | null; open: boolean; onClose: () => void;
  header?: { title: string; subtitle?: string };
  items: ActionItem[];
  /** Text of the phone sheet's Cancel button. */
  cancelLabel: string;
};

/**
 * A page or row actions menu: a popover on desktop, a bottom sheet with Cancel on a phone.
 * Items the user may not use are simply not passed (no disabled state).
 */
export const ActionMenu = ({ label, anchorEl, open, onClose, header, items, cancelLabel }: Props) => {
  const phone = usePhone();
  const uid = useId();
  const titleId = `${uid}-title`;
  const choose = (fn?: () => void) => () => { fn?.(); onClose(); };

  const rendered = items.map((it, i) => {
    if ("divider" in it) return <hr key={`d${i}`} className={s.divider} />;
    if ("radioGroup" in it) {
      const g = it.radioGroup;
      const gid = `${uid}-g${i}`;
      return (
        <div key={`g${i}`}>
          <div id={gid} className={s.groupLabel}>{g.label}</div>
          <div role={phone ? "radiogroup" : "group"} aria-labelledby={gid}>
            {g.options.map((o) => (
              <MenuSurfaceItem key={o.value} selected={o.value === g.value} className={o.value === g.value ? s.chosen : undefined}
                icon={o.value === g.value ? <CheckRoundedIcon /> : <span className={s.blank} />} onClick={choose(() => g.onChange(o.value))}>
                {o.label}
              </MenuSurfaceItem>
            ))}
          </div>
        </div>
      );
    }
    const cls = it.danger ? s.danger : undefined;
    const body = it.hint && it.hintBelow
      ? <span className={s.stack}><span>{it.label}</span><span className={s.hintBelow}>{it.hint}</span></span>
      : <>{it.label}{it.hint && <span className={s.hint}>{it.hint}</span>}</>;
    return it.href
      ? <MenuSurfaceLink key={it.id} href={it.href} icon={it.icon} className={cls} onClick={choose(it.onSelect)}>{body}</MenuSurfaceLink>
      : <MenuSurfaceItem key={it.id} icon={it.icon} className={cls} onClick={choose(it.onSelect)}>{body}</MenuSurfaceItem>;
  });

  return (
    <MenuSurface anchorEl={anchorEl} open={open} onClose={onClose} label={label} labelledBy={header ? titleId : undefined}
      header={header && (
        <div className={s.head}>
          <div id={titleId} className={s.title}>{header.title}</div>
          {header.subtitle && <div className={s.subtitle}>{header.subtitle}</div>}
        </div>
      )}>
      {rendered}
      {phone && <div className={s.cancel}><Button fullWidth onClick={onClose}>{cancelLabel}</Button></div>}
    </MenuSurface>
  );
};
